// CRM Campaigns — full rehouse of classic Campaigns.js + CampaignDetail.js into the CRM
// shell as a single master-detail screen. Persistent left campaign rail (keeps scroll,
// never unmounts) + full detail pane on the right. Preserves every capability:
//   LIST:   search, status chips, progress, reply/bounce counts, + New campaign (2-step
//           wizard with PoolPicker + mailbox rotation set).
//   DETAIL: lifecycle (launch/pause/resume/stop), stats strip, paginated contacts with
//           search + status filter + bulk bar (pause/resume/send-now/skip-step/remove) +
//           conversation modal, full config editor (edit-gated on draft/paused),
//           personas link/unlink, mailboxes add/toggle/remove, danger-zone delete.
// Routing: /clients/:id/crm/campaigns and /clients/:id/crm/campaigns/:campaignId.
// No old-UI routes. Orange reserved for primary actions + selected accent only.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import API_BASE from '../../config';
import { List } from 'react-window';

const CONTACTS_PAGE = 50;
const CARD_STRIDE = 83; // measured card height (75px) + gap (8px); rowHeight for virtualised rail
const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };

// status -> CRM semantic token. draft/completed=slate, scheduled=violet,
// active/running=green, paused=amber, stopped=rose.
const STATUS_CLASS = {
  draft:     'crmca-st--slate',
  scheduled: 'crmca-st--violet',
  active:    'crmca-st--green',
  running:   'crmca-st--green',
  paused:    'crmca-st--amber',
  completed: 'crmca-st--slate',
  stopped:   'crmca-st--rose',
};
function StatusChip({ status }) {
  const cls = STATUS_CLASS[status] || 'crmca-st--slate';
  return <span className={'crmca-st ' + cls}>{status || 'unknown'}</span>;
}

function parseSendDays(str) {
  if (!str) return [];
  if (str === 'mon-fri') return ['mon', 'tue', 'wed', 'thu', 'fri'];
  return str.split(',').map(s => s.trim().toLowerCase()).filter(d => DAY_KEYS.includes(d));
}

function getTimezoneOptions() {
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function') {
      return Intl.supportedValuesOf('timeZone');
    }
  } catch (_) {}
  return [
    'UTC', 'Europe/London', 'Europe/Dublin', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid',
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Toronto', 'America/Sao_Paulo',
    'Asia/Dubai', 'Asia/Singapore', 'Asia/Hong_Kong', 'Asia/Tokyo', 'Asia/Kolkata',
    'Australia/Sydney', 'Pacific/Auckland', 'Africa/Lagos', 'Africa/Johannesburg',
  ];
}

const POOL_PALETTE = [
  { bg: '#EFEBFE', border: '#D6CCFB', fg: '#5B3FD6' },
  { bg: '#E6F1FF', border: '#C2DCFF', fg: '#2E8FFF' },
  { bg: '#E3F7F0', border: '#BEEBDD', fg: '#00A878' },
  { bg: '#FEF4E2', border: '#F7E1B8', fg: '#C77E12' },
  { bg: '#FFE9ED', border: '#FBCAD3', fg: '#F43F5E' },
  { bg: '#EEF1F5', border: '#D9DEE6', fg: '#64748B' },
  { bg: '#FCE7F3', border: '#F6C6E1', fg: '#EC4899' },
  { bg: '#E0F7FB', border: '#BEEBF3', fg: '#06B6D4' },
];
function poolColour(id) {
  const n = typeof id === 'number' ? id : parseInt(id) || 0;
  return POOL_PALETTE[n % POOL_PALETTE.length];
}

function DayPicker({ value, onChange, disabled }) {
  const set = new Set(parseSendDays(value));
  const toggle = (day) => {
    const next = new Set(set);
    if (next.has(day)) next.delete(day); else next.add(day);
    onChange(DAY_KEYS.filter(d => next.has(d)).join(','));
  };
  return (
    <div className="crmca-days">
      {DAY_KEYS.map(d => (
        <button
          type="button"
          key={d}
          disabled={disabled}
          onClick={() => !disabled && toggle(d)}
          className={'crmca-day' + (set.has(d) ? ' crmca-day--on' : '')}
        >
          {DAY_LABELS[d]}
        </button>
      ))}
    </div>
  );
}

function PoolPicker({ pools, onPick }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  if (!pools || pools.length === 0) return null;

  if (pools.length <= 3) {
    return (
      <div className="crmca-poolbar">
        <div className="crmca-poolbar-lbl">Quick add from pool</div>
        <div className="crmca-poolpills">
          {pools.map(p => {
            const c = poolColour(p.id);
            return (
              <button key={p.id} type="button" onClick={() => onPick(p.id)}
                className="crmca-poolpill"
                style={{ background: c.bg, borderColor: c.border, color: c.fg }}>
                + {p.name}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const filtered = pools.filter(p => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (p.name || '').toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q);
  });

  return (
    <div className="crmca-pooldd">
      <div className="crmca-poolbar-lbl">Quick add from pool ({pools.length} available)</div>
      <input
        type="text"
        placeholder="Search pools by name..."
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="crmca-input"
      />
      {open && filtered.length > 0 && (
        <div className="crmca-pooldd-list">
          {filtered.slice(0, 50).map(p => {
            const c = poolColour(p.id);
            const n = p.members?.length ?? p.member_count ?? 0;
            return (
              <button
                key={p.id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); onPick(p.id); setQuery(''); setOpen(false); }}
                className="crmca-pooldd-item"
              >
                <span className="crmca-poolpill" style={{ background: c.bg, borderColor: c.border, color: c.fg }}>{p.name}</span>
                <span className="crmca-pooldd-count">{n} mailbox{n !== 1 ? 'es' : ''}</span>
              </button>
            );
          })}
          {filtered.length > 50 && <div className="crmca-pooldd-more">Showing first 50 of {filtered.length}. Refine search.</div>}
        </div>
      )}
      {open && filtered.length === 0 && <div className="crmca-pooldd-empty">No pools match.</div>}
    </div>
  );
}

// One virtualised rail card. Receives index + style from react-window List,
// plus campaigns/campaignId/onSelect via rowProps. style MUST be applied (v2
// List positions rows absolutely). The 8px gap lives as bottom padding inside
// the row so the 83px stride holds exactly.
function CampaignRow({ index, style, campaigns, campaignId, onSelect }) {
  const c = campaigns[index];
  if (!c) return null;
  const pct = c.total_contacts > 0 ? Math.round(((c.sent_count || 0) / c.total_contacts) * 100) : 0;
  const active = String(c.id) === String(campaignId);
  return (
    <div style={style} className="crmca-rowwrap">
      <button
        className={'crmca-card' + (active ? ' crmca-card--on' : '')}
        onClick={() => onSelect(c.id)}
      >
        <div className="crmca-card-top">
          <span className="crmca-card-name">{c.name}</span>
          <StatusChip status={c.status} />
        </div>
        <div className="crmca-card-bar"><div className="crmca-card-fill" style={{ width: `${pct}%` }} /></div>
        <div className="crmca-card-meta">
          <span>{c.sent_count || 0}/{c.total_contacts || 0} sent</span>
          <span>{c.reply_count || 0} repl · {c.bounce_count || 0} bnc</span>
        </div>
      </button>
    </div>
  );
}

export default function CrmCampaigns() {
  const { clientId } = useOutletContext();
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const base = `/clients/${clientId}/crm/campaigns`;

  const loadList = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/clients/${clientId}/campaigns`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      setCampaigns(await res.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [clientId, getToken]);

  useEffect(() => { if (clientId) loadList(); }, [clientId, loadList]);

  const filtered = campaigns.filter(c => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (c.name || '').toLowerCase().includes(q) || (c.status || '').toLowerCase().includes(q);
  });

  const selectCampaign = (id) => navigate(`${base}/${id}`);

  return (
    <div className="crmca-root">
      <aside className="crmca-rail">
        <div className="crmca-rail-head">
          <div className="crmca-rail-title">Campaigns</div>
          <button className="crmca-newbtn" onClick={() => setCreateOpen(true)}>+ New</button>
        </div>
        <div className="crmca-rail-search">
          <input
            type="text"
            placeholder="Search campaigns..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="crmca-input"
          />
        </div>
        <div className="crmca-rail-list">
          {loading ? (
            <div className="crmca-rail-empty">Loading...</div>
          ) : error ? (
            <div className="crmca-rail-err">Error: {error}</div>
          ) : filtered.length === 0 ? (
            <div className="crmca-rail-empty">
              {campaigns.length === 0 ? 'No campaigns yet.' : 'No matches.'}
            </div>
          ) : (
            <List
              style={{ height: '100%' }}
              rowCount={filtered.length}
              rowHeight={CARD_STRIDE}
              overscanCount={6}
              rowComponent={CampaignRow}
              rowProps={{ campaigns: filtered, campaignId, onSelect: selectCampaign }}
            />
          )}
        </div>
      </aside>

      <main className="crmca-detail">
        {campaignId ? (
          <CampaignDetailPane
            key={campaignId}
            campaignId={campaignId}
            clientId={clientId}
            base={base}
            onListChanged={loadList}
            onDeleted={() => { loadList(); navigate(base); }}
          />
        ) : (
          <div className="crmca-detail-empty">
            <div className="crmca-detail-empty-inner">
              <div className="crmca-detail-empty-title">Select a campaign</div>
              <div className="crmca-detail-empty-sub">Pick a campaign on the left, or create a new one.</div>
            </div>
          </div>
        )}
      </main>

      {createOpen && (
        <CreateCampaignModal
          clientId={clientId}
          onClose={() => setCreateOpen(false)}
          onCreated={(newId) => { setCreateOpen(false); loadList(); navigate(`${base}/${newId}`); }}
        />
      )}
    </div>
  );
}

function CampaignDetailPane({ campaignId, clientId, base, onListChanged, onDeleted }) {
  const { getToken } = useAuth();

  const [campaign, setCampaign] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsQ, setContactsQ] = useState('');
  const [contactsStatus, setContactsStatus] = useState('');
  const [contactsSort, setContactsSort] = useState('last_activity');
  const [stepStats, setStepStats] = useState([]);
  const [activity, setActivity] = useState(null);
  const [contactsOffset, setContactsOffset] = useState(0);
  const [selectedCcIds, setSelectedCcIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [convoOpenContactId, setConvoOpenContactId] = useState(null);
  const [convoData, setConvoData] = useState(null);
  const [convoLoading, setConvoLoading] = useState(false);
  const [campaignMailboxes, setCampaignMailboxes] = useState([]);
  const [mailboxPickerOpen, setMailboxPickerOpen] = useState(false);
  const [linkedPersonas, setLinkedPersonas] = useState([]);
  const [availablePersonas, setAvailablePersonas] = useState([]);
  const [personaToLink, setPersonaToLink] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [timezone, setTimezone] = useState('');
  const [sendStart, setSendStart] = useState('09:00');
  const [sendEnd, setSendEnd] = useState('17:30');
  const [sendDays, setSendDays] = useState('mon,tue,wed,thu,fri');
  const [throttlePerHour, setThrottlePerHour] = useState(30);
  const [throttlePerDomain, setThrottlePerDomain] = useState(5);
  const [minGap, setMinGap] = useState(60);
  const [pauseOnReply, setPauseOnReply] = useState(true);
  const [pauseOnBounce, setPauseOnBounce] = useState(true);
  const [replyTo, setReplyTo] = useState('');
  const [trackOpens, setTrackOpens] = useState(true);
  const [trackClicks, setTrackClicks] = useState(false);
  const [recipientTz, setRecipientTz] = useState(true);
  const [stepWaitDays, setStepWaitDays] = useState([2, 3, 4, 5]);

  const tzOptions = useMemo(() => getTimezoneOptions(), []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(`${API_BASE}/campaigns/${campaignId}`, { headers });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const data = await res.json();
      setCampaign(data);
      setName(data.name || '');
      setDescription(data.description || '');
      setTimezone(data.timezone || 'Europe/London');
      setSendStart((data.send_window_start || '09:00:00').slice(0, 5));
      setSendEnd((data.send_window_end || '17:30:00').slice(0, 5));
      setSendDays(data.send_days || 'mon,tue,wed,thu,fri');
      setThrottlePerHour(data.throttle_per_hour ?? 30);
      setThrottlePerDomain(data.throttle_per_domain_per_hour ?? 5);
      setMinGap(data.min_gap_seconds ?? 60);
      setPauseOnReply(!!data.pause_on_reply);
      setPauseOnBounce(!!data.pause_on_bounce);
      setReplyTo(data.reply_to_email || '');
      setTrackOpens(data.track_opens !== false);
      setTrackClicks(!!data.track_clicks);
      setRecipientTz(data.send_in_recipient_tz !== false);
      setStepWaitDays(Array.isArray(data.default_step_wait_days) && data.default_step_wait_days.length ? data.default_step_wait_days : [2, 3, 4, 5]);

      const ssres = await fetch(`${API_BASE}/campaigns/${campaignId}/step-stats`, { headers });
      if (ssres.ok) { const ss = await ssres.json(); setStepStats(ss.steps || []); }

      const mbres = await fetch(`${API_BASE}/campaigns/${campaignId}/mailboxes`, { headers });
      if (mbres.ok) setCampaignMailboxes(await mbres.json());

      const [lpRes, apRes] = await Promise.all([
        fetch(`${API_BASE}/campaigns/${campaignId}/personas`, { headers }),
        fetch(`${API_BASE}/clients/${clientId || data.client_id}/personas`, { headers }),
      ]);
      if (lpRes.ok) setLinkedPersonas(await lpRes.json());
      if (apRes.ok) setAvailablePersonas(await apRes.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [campaignId, getToken, clientId]);

  const loadContacts = useCallback(async () => {
    setContactsLoading(true);
    try {
      const token = await getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const p = new URLSearchParams();
      p.set('limit', String(CONTACTS_PAGE));
      p.set('offset', String(contactsOffset));
      if (contactsQ.trim()) p.set('q', contactsQ.trim());
      if (contactsStatus) p.set('status', contactsStatus);
      if (contactsSort) p.set('sort', contactsSort);
      const r = await fetch(`${API_BASE}/campaigns/${campaignId}/contacts?${p.toString()}`, { headers });
      if (r.ok) {
        const data = await r.json();
        setContacts(data.rows || []);
        setContactsTotal(data.total || 0);
        const visible = new Set((data.rows || []).map(r2 => r2.id));
        setSelectedCcIds(prev => new Set([...prev].filter(id => visible.has(id))));
      }
    } finally { setContactsLoading(false); }
  }, [campaignId, contactsOffset, contactsQ, contactsStatus, contactsSort, getToken]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => { loadContacts(); }, 250);
    return () => clearTimeout(t);
  }, [loadContacts]);

  useEffect(() => {
    if (!convoOpenContactId) { setConvoData(null); setActivity(null); return; }
    let cancelled = false;
    (async () => {
      setConvoLoading(true);
      try {
        const token = await getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const [r, ar] = await Promise.all([
          fetch(`${API_BASE}/conversations/by-contact/${convoOpenContactId}?campaign_id=${campaignId}`, { headers }),
          fetch(`${API_BASE}/campaigns/${campaignId}/contacts/${convoOpenContactId}/activity`, { headers }),
        ]);
        if (ar.ok) { const ad = await ar.json(); if (!cancelled) setActivity(ad.items || []); }
        if (r.ok) { const data = await r.json(); if (!cancelled) setConvoData(data); }
      } finally { if (!cancelled) setConvoLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [convoOpenContactId, campaignId, getToken]);

  async function runBulk(action) {
    if (selectedCcIds.size === 0) return;
    if (action === 'send-now' || action === 'resume') {
      const replied = contacts.filter(c => selectedCcIds.has(c.id) && c.replied_at);
      if (replied.length && !window.confirm(`${replied.length === 1 ? (replied[0].full_name || 'This contact') + ' has' : replied.length + ' of these contacts have'} already replied. ${action === 'send-now' ? 'Send the next step' : 'Resume the sequence'} anyway? If they reply again they are paused again.`)) return;
    }
    if (action === 'remove' && !window.confirm(`Remove ${selectedCcIds.size} contact(s) from this campaign? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const token = await getToken();
      const headers = { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' };
      const r = await fetch(`${API_BASE}/campaigns/${campaignId}/contacts/bulk-action`, {
        method: 'POST', headers,
        body: JSON.stringify({ action, campaign_contact_ids: [...selectedCcIds] }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); toast.error('Bulk action failed: ' + (e.error || r.status)); }
      else toast.success(`${action} applied to ${selectedCcIds.size}`);
      setSelectedCcIds(new Set());
      await loadContacts();
      await load();
      onListChanged && onListChanged();
    } finally { setBulkBusy(false); }
  }

  function toggleSelectAll() {
    if (selectedCcIds.size === contacts.length && contacts.length > 0) setSelectedCcIds(new Set());
    else setSelectedCcIds(new Set(contacts.map(c => c.id)));
  }
  function toggleSelectOne(id) {
    setSelectedCcIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }

  const save = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/campaigns/${campaignId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name, description, timezone,
          send_window_start: sendStart, send_window_end: sendEnd, send_days: sendDays,
          throttle_per_hour: throttlePerHour, throttle_per_domain_per_hour: throttlePerDomain,
          min_gap_seconds: minGap, pause_on_reply: pauseOnReply, pause_on_bounce: pauseOnBounce,
          default_step_wait_days: stepWaitDays,
          reply_to_email: replyTo.trim() || null,
          track_opens: trackOpens, track_clicks: trackClicks, send_in_recipient_tz: recipientTz,
        }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `Save failed (${res.status})`); }
      toast.success('Saved');
      setEditing(false);
      await load();
      onListChanged && onListChanged();
    } catch (e) { toast.error(e.message); }
  };

  // Launch / resume go through a preflight: every enrolled contact is checked with the
  // same rules the scheduler applies, so problems show before anything sends.
  const [preflight, setPreflight] = useState(null); // null | { mode, loading, data, error, launching }
  const openPreflight = async (mode) => {
    setPreflight({ mode, loading: true });
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/campaigns/${campaignId}/preflight`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Check failed (${res.status})`);
      setPreflight({ mode, loading: false, data });
    } catch (e) { setPreflight({ mode, loading: false, error: e.message }); }
  };
  const confirmLaunch = async () => {
    if (!preflight) return;
    setPreflight(p => ({ ...p, launching: true }));
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/campaigns/${campaignId}/${preflight.mode === 'resume' ? 'resume' : 'start'}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Launch failed (${res.status})`);
      toast.success(preflight.mode === 'resume' ? 'Campaign resumed' : 'Campaign launched');
      setPreflight(null);
      await load();
      onListChanged && onListChanged();
    } catch (e) { setPreflight(p => ({ ...p, launching: false, error: e.message })); }
  };

  const action = async (path, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/campaigns/${campaignId}${path}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Action failed (${res.status})`);
      await load();
      onListChanged && onListChanged();
    } catch (e) { toast.error(e.message); }
  };

  const removeMailbox = async (mailboxId, emailLabel) => {
    if (!window.confirm(`Remove ${emailLabel} from this campaign? Pending sends from this mailbox will reassign to others.`)) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/campaigns/${campaignId}/mailboxes/${mailboxId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Remove failed (${res.status})`);
      await load();
    } catch (e) { toast.error(e.message); }
  };

  const toggleMailboxActive = async (mailboxId, currentActive) => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/campaigns/${campaignId}/mailboxes/${mailboxId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !currentActive }),
      });
      if (!res.ok) throw new Error(`Toggle failed (${res.status})`);
      await load();
    } catch (e) { toast.error(e.message); }
  };

  const linkPersona = async () => {
    if (!personaToLink) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/campaigns/${campaignId}/personas/${personaToLink}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      setPersonaToLink('');
      await load();
    } catch (e) { toast.error('Could not link persona'); }
  };

  const unlinkPersona = async (personaId, label) => {
    if (!window.confirm(`Unlink "${label}" from this campaign? Sending will fall back to the direct mailbox list below.`)) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/campaigns/${campaignId}/personas/${personaId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      await load();
    } catch (e) { toast.error('Could not unlink persona'); }
  };

  const hardDelete = async () => {
    if (!window.confirm('PERMANENT: delete this campaign and all its contact state?')) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/campaigns/${campaignId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      toast.success('Campaign deleted');
      onDeleted && onDeleted();
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <div className="crmca-detail-empty"><div className="crmca-detail-empty-inner">Loading campaign...</div></div>;
  if (error) return <div className="crmca-detail-empty"><div className="crmca-detail-empty-inner crmca-err">Error: {error}</div></div>;
  if (!campaign) return <div className="crmca-detail-empty"><div className="crmca-detail-empty-inner">Campaign not found.</div></div>;

  const stats = campaign.stats || {};
  const status = campaign.status;
  const isDraft = status === 'draft';
  const isActive = status === 'active' || status === 'scheduled';
  const isPaused = status === 'paused';
  const isTerminal = status === 'completed' || status === 'stopped';
  const canEdit = isDraft || isPaused;
  const completionPct = stats.total > 0 ? Math.round((Number(stats.completed || 0) / Number(stats.total || 1)) * 100) : 0;

  return (
    <div className="crmca-pane">
      {preflight && (
        <PreflightModal
          state={preflight}
          clientId={clientId || campaign.client_id}
          onClose={() => !preflight.launching && setPreflight(null)}
          onConfirm={confirmLaunch}
        />
      )}
      <div className="crmca-pane-head">
        <div className="crmca-pane-title">
          <h2>{campaign.name}</h2>
          <StatusChip status={status} />
        </div>
        <div className="crmca-pane-sub">
          {campaign.pool_name && <><span>Pool: {campaign.pool_name}</span><span className="crmca-dot">·</span></>}
          {campaign.mailbox_email && <><span>Mailbox: {campaign.mailbox_email}</span><span className="crmca-dot">·</span></>}
          <span>Created {new Date(campaign.created_at).toLocaleDateString('en-GB')}</span>
          {campaign.started_at && <><span className="crmca-dot">·</span><span>Started {new Date(campaign.started_at).toLocaleDateString('en-GB')}</span></>}
        </div>
      </div>

      <div className="crmca-actions">
        {isDraft && <button onClick={() => openPreflight('start')} className="crmca-btn crmca-btn--primary">Launch</button>}
        {isActive && (
          <>
            <button onClick={() => action('/pause', 'Pause this campaign?')} className="crmca-btn">Pause</button>
            <button onClick={() => action('/stop', 'Stop this campaign? This cannot be undone.')} className="crmca-btn crmca-btn--danger">Stop</button>
          </>
        )}
        {isPaused && (
          <>
            <button onClick={() => openPreflight('resume')} className="crmca-btn crmca-btn--primary">Resume</button>
            <button onClick={() => action('/stop', 'Stop this campaign? This cannot be undone.')} className="crmca-btn crmca-btn--danger">Stop</button>
          </>
        )}
        {isTerminal && <span className="crmca-terminal-note">Campaign {status}. No further actions.</span>}
      </div>

      <div className="crmca-stats" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))' }}>
        <Stat label="Total" value={stats.total ?? 0} />
        <Stat label="Sent" value={campaign.sent_count ?? 0} sub={`${completionPct}% complete`} accent="var(--crm-green)" />
        <Stat label="Pending" value={stats.pending ?? 0} />
        <Stat label="In progress" value={stats.in_progress ?? 0} />
      </div>
      <div className="crmca-stats" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', marginTop: 8 }}>
        {[
          { key: 'contacted', label: 'Contacted', n: stats.contacted },
          { key: 'opened', label: 'Opened', n: stats.opened, off: campaign.track_opens === false },
          { key: 'clicked', label: 'Clicked', n: stats.clicked, off: !campaign.track_clicks },
          { key: 'replied', label: 'Replied', n: stats.replied, accent: 'var(--crm-blue)' },
          { key: 'bounced', label: 'Bounced', n: stats.bounced, accent: 'var(--crm-rose)' },
        ].map(t => {
          const on = contactsStatus === t.key;
          const contacted = Number(stats.contacted || 0);
          const sub = t.off ? 'Tracking off'
            : (t.key !== 'contacted' && contacted > 0 ? `${Math.round((Number(t.n || 0) / contacted) * 100)}% · click to list` : 'Click to list');
          const pick = () => { setContactsOffset(0); setContactsStatus(on ? '' : t.key); };
          return (
            <div key={t.key} role="button" tabIndex={0} onClick={pick} onKeyDown={e => { if (e.key === 'Enter') pick(); }}
              style={{ cursor: 'pointer', outline: on ? '2px solid var(--crm-orange)' : 'none', borderRadius: 8 }}>
              <Stat label={t.label} value={t.off ? '—' : (t.n ?? 0)} sub={sub} accent={t.accent} />
            </div>
          );
        })}
      </div>

      {stepStats.length > 0 && (
        <Section title="By step">
          <table className="crmca-tbl">
            <thead><tr><th>Step</th><th>Sent</th><th>Opened</th><th>Clicked</th><th>Replied</th></tr></thead>
            <tbody>
              {stepStats.map(st => {
                const pc = (n) => (st.sent > 0 && n > 0 ? ` · ${Math.round((n / st.sent) * 100)}%` : '');
                return (
                  <tr key={st.step}>
                    <td>Step {st.step}</td>
                    <td>{st.sent}</td>
                    <td>{campaign.track_opens === false ? '—' : `${st.opened}${pc(st.opened)}`}</td>
                    <td>{campaign.track_clicks ? `${st.clicked}${pc(st.clicked)}` : '—'}</td>
                    <td>{st.replied}{pc(st.replied)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Section>
      )}

      <PlacementSection campaignId={campaignId} />

      <Section
        title={`Contacts (${contactsTotal})`}
        right={
          <div className="crmca-sec-tools">
            <input
              value={contactsQ}
              onChange={e => { setContactsOffset(0); setContactsQ(e.target.value); }}
              placeholder="Search name, email, company..."
              className="crmca-input crmca-input--sm"
            />
            <select
              value={['pending', 'in_progress', 'paused', 'completed'].includes(contactsStatus) ? contactsStatus : ''}
              onChange={e => { setContactsOffset(0); setContactsStatus(e.target.value); }}
              className="crmca-select crmca-select--sm"
            >
              <option value="">All status</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In progress</option>
              <option value="paused">Paused</option>
              <option value="completed">Completed</option>
            </select>
            <select value={contactsSort} onChange={e => { setContactsOffset(0); setContactsSort(e.target.value); }} className="crmca-select crmca-select--sm">
              <option value="last_activity">Sort: last activity</option>
              <option value="most_opens">Sort: most opens</option>
              <option value="most_clicks">Sort: most clicks</option>
              <option value="name">Sort: name</option>
              <option value="">Sort: date added</option>
            </select>
          </div>
        }
      >
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          {[
            { key: '', label: 'All', n: stats.total },
            { key: 'opened', label: 'Opened', n: stats.opened },
            { key: 'not_opened', label: 'Not opened', n: stats.not_opened },
            { key: 'clicked', label: 'Clicked', n: stats.clicked },
            { key: 'replied', label: 'Replied', n: stats.replied },
            { key: 'bounced', label: 'Bounced', n: stats.bounced },
          ].map(ch => (
            <button key={ch.key || 'all'}
              onClick={() => { setContactsOffset(0); setContactsStatus(ch.key); }}
              className={'crmca-btn crmca-btn--sm' + (contactsStatus === ch.key ? ' crmca-btn--primary' : '')}>
              {ch.label} {ch.n ?? 0}
            </button>
          ))}
        </div>
        {selectedCcIds.size > 0 && (
          <div className="crmca-bulkbar">
            <span className="crmca-bulkbar-n">{selectedCcIds.size} selected</span>
            <span className="crmca-dot">·</span>
            <button onClick={() => runBulk('pause')} disabled={bulkBusy} className="crmca-btn crmca-btn--sm">Pause</button>
            <button onClick={() => runBulk('resume')} disabled={bulkBusy} className="crmca-btn crmca-btn--sm">Resume</button>
            <button onClick={() => runBulk('send-now')} disabled={bulkBusy} className="crmca-btn crmca-btn--sm">Send now</button>
            <button onClick={() => runBulk('skip-step')} disabled={bulkBusy} className="crmca-btn crmca-btn--sm">Skip step</button>
            <button onClick={() => runBulk('remove')} disabled={bulkBusy} className="crmca-btn crmca-btn--sm crmca-btn--dangersolid">Remove</button>
            <span className="crmca-spacer" />
            <button onClick={() => setSelectedCcIds(new Set())} className="crmca-btn crmca-btn--sm crmca-btn--ghost">Clear</button>
          </div>
        )}
        {contactsLoading && contacts.length === 0 ? (
          <div className="crmca-muted">Loading...</div>
        ) : contactsTotal === 0 ? (
          <div className="crmca-muted">{contactsQ || contactsStatus ? 'No contacts match the current filter.' : 'No contacts enrolled yet.'}</div>
        ) : (
          <>
            <table className="crmca-tbl">
              <thead>
                <tr>
                  <th className="crmca-tbl-chk"><input type="checkbox" checked={selectedCcIds.size === contacts.length && contacts.length > 0} onChange={toggleSelectAll} /></th>
                  <th>Contact</th><th>Status</th><th>Step</th><th>Opened</th><th>Clicked</th><th>Replied</th><th>Next send</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map(cc => (
                  <tr
                    key={cc.id}
                    onClick={() => setConvoOpenContactId(cc.contact_id)}
                    className={'crmca-tbl-row' + (selectedCcIds.has(cc.id) ? ' crmca-tbl-row--sel' : '')}
                  >
                    <td onClick={e => e.stopPropagation()}><input type="checkbox" checked={selectedCcIds.has(cc.id)} onChange={() => toggleSelectOne(cc.id)} /></td>
                    <td>
                      <div className="crmca-tbl-name">{cc.full_name || `Contact ${cc.contact_id}`}</div>
                      <div className="crmca-tbl-sub">{cc.email}{cc.company_name ? ` · ${cc.company_name}` : ''}</div>
                    </td>
                    <td><StatusChip status={cc.status} /></td>
                    <td>{cc.current_step ?? 0}</td>
                    <td>{cc.opened_at
                      ? <><div className="crmca-tbl-name" style={{ color: 'var(--crm-green)' }}>{cc.open_count || 1}×</div><div className="crmca-tbl-sub">Last {fmtShort(cc.last_opened_at || cc.opened_at)}</div></>
                      : <span className="crmca-tbl-muted">—</span>}</td>
                    <td>{cc.clicked_at
                      ? <><div className="crmca-tbl-name" style={{ color: 'var(--crm-green)' }}>{cc.click_count || 1}×</div><div className="crmca-tbl-sub" title={cc.last_clicked_url || ''}>{linkLabel(cc.last_clicked_url)}</div></>
                      : <span className="crmca-tbl-muted">—</span>}</td>
                    <td>{cc.replied_at
                      ? <><div className="crmca-tbl-name" style={{ color: 'var(--crm-blue)' }}>Yes</div><div className="crmca-tbl-sub">{fmtShort(cc.replied_at)}</div></>
                      : <span className="crmca-tbl-muted">—</span>}</td>
                    <td className="crmca-tbl-muted">{cc.next_send_at ? new Date(cc.next_send_at).toLocaleString('en-GB') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="crmca-pager">
              <div>Showing {contactsTotal === 0 ? 0 : contactsOffset + 1}-{Math.min(contactsOffset + contacts.length, contactsTotal)} of {contactsTotal}</div>
              <div className="crmca-pager-btns">
                <button onClick={() => setContactsOffset(Math.max(0, contactsOffset - CONTACTS_PAGE))} disabled={contactsOffset === 0 || contactsLoading} className="crmca-btn crmca-btn--sm">Previous</button>
                <button onClick={() => setContactsOffset(contactsOffset + CONTACTS_PAGE)} disabled={contactsOffset + CONTACTS_PAGE >= contactsTotal || contactsLoading} className="crmca-btn crmca-btn--sm">Next</button>
              </div>
            </div>
          </>
        )}
      </Section>

      <Section
        title="Configuration"
        right={
          canEdit ? (
            editing ? (
              <div className="crmca-sec-tools">
                <button onClick={save} className="crmca-btn crmca-btn--sm crmca-btn--primary">Save</button>
                <button onClick={() => { setEditing(false); load(); }} className="crmca-btn crmca-btn--sm">Cancel</button>
              </div>
            ) : (
              <button onClick={() => setEditing(true)} className="crmca-btn crmca-btn--sm">Edit</button>
            )
          ) : (
            <span className="crmca-ro">Read-only ({status})</span>
          )
        }
      >
        <div className="crmca-fields">
          <Field label="Name">{editing ? <input value={name} onChange={(e) => setName(e.target.value)} className="crmca-input" /> : campaign.name}</Field>
          <Field label="Timezone">
            {editing ? (
              <select value={timezone} onChange={(e) => setTimezone(e.target.value)} className="crmca-select">
                {tzOptions.map(tz => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            ) : campaign.timezone}
          </Field>
          <Field label="Send window">
            {editing ? (
              <span className="crmca-timewin">
                <input type="time" value={sendStart} onChange={(e) => setSendStart(e.target.value)} className="crmca-input crmca-input--time" />
                <span>to</span>
                <input type="time" value={sendEnd} onChange={(e) => setSendEnd(e.target.value)} className="crmca-input crmca-input--time" />
              </span>
            ) : `${sendStart} – ${sendEnd}${recipientTz ? " in each contact's timezone" : ''}`}
          </Field>
          <Field label="Throttle / hour">{editing ? <input type="number" value={throttlePerHour} onChange={(e) => setThrottlePerHour(parseInt(e.target.value) || 0)} className="crmca-input" /> : campaign.throttle_per_hour}</Field>
          <Field label="Throttle / domain / hour">{editing ? <input type="number" value={throttlePerDomain} onChange={(e) => setThrottlePerDomain(parseInt(e.target.value) || 0)} className="crmca-input" /> : campaign.throttle_per_domain_per_hour}</Field>
          <Field label="Min gap (seconds)">{editing ? <input type="number" value={minGap} onChange={(e) => setMinGap(parseInt(e.target.value) || 0)} className="crmca-input" /> : campaign.min_gap_seconds}</Field>
          <Field label="Reply-to">{editing
            ? <input value={replyTo} onChange={(e) => setReplyTo(e.target.value)} className="crmca-input" placeholder="Leave empty to get replies on the sending mailbox" />
            : (campaign.reply_to_email || 'Sending mailbox')}</Field>
          <Field label="Description">{editing ? <input value={description} onChange={(e) => setDescription(e.target.value)} className="crmca-input" /> : (campaign.description || '—')}</Field>
        </div>

        <div className="crmca-cfg-block">
          <div className="crmca-cfg-lbl">Step delays (days between steps)</div>
          <div className="crmca-steps">
            {stepWaitDays.map((d, i) => (
              <span key={i} className="crmca-step">
                Step {i + 1} → {i + 2}:
                {editing ? (
                  <input type="number" min={0} max={90} value={d}
                    onChange={(e) => { const v = Math.max(0, Math.min(90, parseInt(e.target.value) || 0)); setStepWaitDays(prev => prev.map((x, idx) => idx === i ? v : x)); }}
                    className="crmca-input crmca-input--num" />
                ) : <strong>{d}</strong>}
                <span className="crmca-step-d">d</span>
              </span>
            ))}
            {editing && (
              <>
                <button type="button" onClick={() => setStepWaitDays(prev => [...prev, prev[prev.length - 1] || 3])} className="crmca-btn crmca-btn--xs">+ Add gap</button>
                {stepWaitDays.length > 1 && <button type="button" onClick={() => setStepWaitDays(prev => prev.slice(0, -1))} className="crmca-btn crmca-btn--xs crmca-btn--ghost">− Remove last</button>}
              </>
            )}
          </div>
          <div className="crmca-cfg-hint">Applies to every contact in this campaign unless a sequence step overrides it.</div>
        </div>

        <div className="crmca-cfg-block">
          <div className="crmca-cfg-lbl">Send days</div>
          <DayPicker value={sendDays} onChange={setSendDays} disabled={!editing} />
        </div>

        <div className="crmca-toggles">
          <label className={editing ? '' : 'crmca-toggle--ro'} title="Uses the contact's location, then the company's. Contacts with no readable location use the campaign timezone.">
            <input type="checkbox" checked={recipientTz} onChange={(e) => setRecipientTz(e.target.checked)} disabled={!editing} />
            Send in each contact's timezone
          </label>
          <label className={editing ? '' : 'crmca-toggle--ro'}>
            <input type="checkbox" checked={trackOpens} onChange={(e) => setTrackOpens(e.target.checked)} disabled={!editing} />
            Track opens
          </label>
          <label className={editing ? '' : 'crmca-toggle--ro'} title="Links are routed through Sonar. Can hurt deliverability on cold mail; scanners are filtered out.">
            <input type="checkbox" checked={trackClicks} onChange={(e) => setTrackClicks(e.target.checked)} disabled={!editing} />
            Track clicks
          </label>
          <label className={editing ? '' : 'crmca-toggle--ro'}>
            <input type="checkbox" checked={pauseOnReply} onChange={(e) => setPauseOnReply(e.target.checked)} disabled={!editing} />
            Pause contact on reply
          </label>
          <label className={editing ? '' : 'crmca-toggle--ro'}>
            <input type="checkbox" checked={pauseOnBounce} onChange={(e) => setPauseOnBounce(e.target.checked)} disabled={!editing} />
            Pause contact on bounce
          </label>
        </div>
      </Section>

      <Section title={`Sending persona${linkedPersonas.length === 1 ? '' : 's'}${linkedPersonas.length ? ` (${linkedPersonas.length})` : ''}`}>
        {linkedPersonas.length > 0 ? (
          <>
            <div className="crmca-note crmca-note--green">Sending routes through the linked persona. The engine rotates across the persona's backing mailboxes; the direct mailbox list below is bypassed while a persona is linked.</div>
            {linkedPersonas.map(p => (
              <div key={p.id} className="crmca-persona">
                <div>
                  <span className="crmca-persona-name">{p.display_name}</span>
                  <div className="crmca-persona-sub">
                    {p.job_title ? `${p.job_title} · ` : ''}{p.mailbox_count} backing mailbox{p.mailbox_count === '1' ? '' : 'es'}
                    {Number(p.mailbox_count) === 0 && <span className="crmca-warn"> — no mailboxes, will not send</span>}
                  </div>
                </div>
                <button onClick={() => unlinkPersona(p.id, p.display_name)} className="crmca-linkbtn crmca-linkbtn--danger">Unlink</button>
              </div>
            ))}
          </>
        ) : (
          <div className="crmca-muted">No persona linked. Sending uses the mailbox list below. Link a persona to send under one identity across rotating mailboxes.</div>
        )}
        <div className="crmca-persona-add">
          <select value={personaToLink} onChange={e => setPersonaToLink(e.target.value)} className="crmca-select">
            <option value="">Select a persona to link...</option>
            {availablePersonas.filter(p => !new Set(linkedPersonas.map(lp => lp.id)).has(p.id)).map(p => (
              <option key={p.id} value={p.id}>{p.display_name} ({p.mailbox_count} mailbox{p.mailbox_count === '1' ? '' : 'es'})</option>
            ))}
          </select>
          <button onClick={linkPersona} disabled={!personaToLink} className={'crmca-btn crmca-btn--sm' + (personaToLink ? ' crmca-btn--primary' : '')}>Link persona</button>
        </div>
        {availablePersonas.length === 0 && <div className="crmca-cfg-hint">No personas for this client yet. Create one under Personas.</div>}
      </Section>

      <Section
        title={`Sending mailboxes (${campaignMailboxes.filter(m => m.active).length} active / ${campaignMailboxes.length} total)`}
        right={<button onClick={() => setMailboxPickerOpen(true)} className="crmca-btn crmca-btn--sm crmca-btn--primary">+ Add mailboxes</button>}
      >
        {isActive && <div className="crmca-note crmca-note--amber">Campaign is active. Removing a mailbox will redistribute its remaining sends to others. New mailboxes added now will start rotating immediately.</div>}
        {campaignMailboxes.length === 0 ? (
          <div className="crmca-muted">No mailboxes attached. Add at least one to send from.</div>
        ) : (
          <table className="crmca-tbl">
            <thead>
              <tr><th>Email</th><th>Provider</th><th>Owner</th><th>Status</th><th>Daily cap</th><th>Sent today</th><th>In campaign</th><th></th></tr>
            </thead>
            <tbody>
              {campaignMailboxes.map(m => (
                <tr key={m.id} className={m.active ? '' : 'crmca-tbl-row--dim'}>
                  <td>{m.email_address}{m.display_name && <span className="crmca-tbl-sub"> — {m.display_name}</span>}</td>
                  <td className="crmca-cap">{m.provider}</td>
                  <td className="crmca-cap">{m.owner_type || 'client'}</td>
                  <td><span className={'crmca-mbstatus crmca-mbstatus--' + (m.status === 'active' ? 'ok' : m.status === 'error' ? 'err' : 'idle')}>{m.status || 'unknown'}</span></td>
                  <td>{m.daily_cap ?? '—'}</td>
                  <td>{m.sent_today ?? 0}</td>
                  <td><button onClick={() => toggleMailboxActive(m.mailbox_id, m.active)} className={'crmca-linkbtn' + (m.active ? ' crmca-linkbtn--ok' : '')}>{m.active ? 'Active' : 'Paused'}</button></td>
                  <td className="crmca-tbl-right"><button onClick={() => removeMailbox(m.mailbox_id, m.email_address)} className="crmca-linkbtn crmca-linkbtn--danger">Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="crmca-cfg-hint">Click "In campaign" status to pause/resume an individual mailbox without removing it. Sends rotate across all active mailboxes.</div>
      </Section>

      <Section title="Danger zone">
        <div className="crmca-muted crmca-danger-copy">Permanently delete this campaign and all its contact state. This cannot be undone.</div>
        <button onClick={hardDelete} className="crmca-btn crmca-btn--dangersolid">Delete campaign</button>
      </Section>

      {mailboxPickerOpen && (
        <AddMailboxesModal
          clientId={clientId || campaign.client_id}
          campaignId={campaignId}
          existingMailboxIds={new Set(campaignMailboxes.map(m => m.mailbox_id))}
          onClose={() => setMailboxPickerOpen(false)}
          onAdded={async () => { setMailboxPickerOpen(false); await load(); }}
        />
      )}

      {convoOpenContactId && (
        <ConversationModal
          convoData={convoData}
          convoLoading={convoLoading}
          activity={activity}
          onClose={() => setConvoOpenContactId(null)}
        />
      )}
    </div>
  );
}

function PreflightModal({ state, clientId, onClose, onConfirm }) {
  const { mode, loading, data, error, launching } = state;
  const verb = mode === 'resume' ? 'Resume' : 'Launch';
  return (
    <div className="crmr-modal-overlay" onClick={onClose}>
      <div className="crmca-pf" onClick={e => e.stopPropagation()}>
        <h2>{verb} check</h2>
        {loading && <div className="crmca-pf-muted">Checking every enrolled contact…</div>}
        {!loading && data && (
          <>
            <div className="crmca-pf-tiles">
              <div className="crmca-pf-tile crmca-pf-tile--green"><b>{data.contacts.ready}</b><span>ready to send</span></div>
              <div className={'crmca-pf-tile' + (data.contacts.blocked ? ' crmca-pf-tile--amber' : '')}><b>{data.contacts.blocked}</b><span>will be held</span></div>
              <div className="crmca-pf-tile"><b>{data.mailboxes.active}</b><span>active mailbox{data.mailboxes.active === 1 ? '' : 'es'}</span></div>
              <div className="crmca-pf-tile"><b>{data.capacity_today}</b><span>sends left today</span></div>
            </div>
            {data.warnings.map((w, i) => <div key={i} className="crmca-note crmca-note--amber">{w}</div>)}
            {data.blocked.length > 0 && (
              <div className="crmca-pf-list">
                <div className="crmca-pf-h">Held back, and why</div>
                {data.blocked.map(b => (
                  <div key={b.reason} className="crmca-pf-row">
                    <div className="crmca-pf-row-top"><span>{b.label}</span><b>{b.count}</b></div>
                    <div className="crmca-pf-ex">
                      {b.examples.map(x => <a key={x.contact_id} href={`/clients/${clientId}/crm/contacts/${x.contact_id}`} target="_blank" rel="noreferrer">{x.name}</a>)}
                      {b.count > b.examples.length && <span>and {b.count - b.examples.length} more</span>}
                    </div>
                  </div>
                ))}
                <div className="crmca-pf-muted">Held contacts are paused with the reason, not dropped. Fix them and resume them from the contact list.</div>
              </div>
            )}
            {data.contacts.checked === 0 && <div className="crmca-note crmca-note--amber">No pending contacts on this campaign.</div>}
          </>
        )}
        {error && <div className="crmca-err crmca-pf-err">{error}</div>}
        <div className="crmr-modal-actions">
          <button className="crmr-act crmr-act-ghost" onClick={onClose} disabled={launching}>Cancel</button>
          <button className="crmr-act crmr-act-primary" onClick={onConfirm} disabled={loading || launching || !data || !data.can_launch}>
            {launching ? `${verb === 'Resume' ? 'Resuming' : 'Launching'}…` : data && data.contacts.blocked ? `${verb} with ${data.contacts.ready}` : verb}
          </button>
        </div>
      </div>
    </div>
  );
}

function fmtShort(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}
function linkLabel(url) {
  if (!url) return 'Link';
  try {
    const u = new URL(url);
    const path = u.pathname.length > 1 ? u.pathname.slice(0, 18) + (u.pathname.length > 18 ? '…' : '') : '';
    return u.hostname.replace(/^www\./, '') + path;
  } catch (_) { return url.slice(0, 30); }
}

// One contact's activity in this campaign: sends, opens, clicks, scanner hits, replies.
function ActivityTimeline({ items }) {
  if (!items) return null;
  if (!items.length) return <div className="crmca-muted">No activity yet.</div>;
  const row = (it, i) => {
    const step = it.step != null ? `Step ${it.step} ` : '';
    let text, color;
    if (it.type === 'sent') { text = `${step}sent · ${fmtShort(it.at)}${it.from ? ' · from ' + it.from : ''}`; color = 'var(--crm-ink)'; }
    else if (it.type === 'opened') {
      const when = it.count > 1 ? `${it.count}× · first ${fmtShort(it.at)}, last ${fmtShort(it.last_at)}` : fmtShort(it.at);
      text = `Opened ${when}${it.clients && it.clients.length ? ' · ' + it.clients.join(', ') : ''}`; color = 'var(--crm-green)';
    }
    else if (it.type === 'clicked') { text = `Clicked ${linkLabel(it.url)} · ${fmtShort(it.at)}`; color = 'var(--crm-green)'; }
    else if (it.type === 'scanner') { text = `${it.count} security scanner ${it.count === 1 ? 'hit' : 'hits'} ignored · ${fmtShort(it.at)}`; color = 'var(--crm-faint)'; }
    else if (it.type === 'replied') { text = `Replied · ${fmtShort(it.at)}`; color = 'var(--crm-blue)'; }
    else { text = it.type; color = 'var(--crm-muted)'; }
    return <div key={i} style={{ color, fontSize: 13, lineHeight: 1.9 }} title={it.url || ''}>{text}</div>;
  };
  return <div>{items.map(row)}</div>;
}

// Inbox placement: send the campaign's real first email to seed inboxes and show where it landed.
const PLACE_META = {
  inbox:       { label: 'Inbox',          cls: 'crmca-st--green' },
  other_tab:   { label: 'Inbox (Other)',  cls: 'crmca-st--green' },
  promotions:  { label: 'Promotions',     cls: 'crmca-st--amber' },
  updates:     { label: 'Updates',        cls: 'crmca-st--amber' },
  social:      { label: 'Social',         cls: 'crmca-st--amber' },
  forums:      { label: 'Forums',         cls: 'crmca-st--amber' },
  other:       { label: 'Other folder',   cls: 'crmca-st--amber' },
  spam:        { label: 'Spam',           cls: 'crmca-st--rose' },
  trash:       { label: 'Trash',          cls: 'crmca-st--rose' },
  missing:     { label: 'Not delivered',  cls: 'crmca-st--rose' },
  send_failed: { label: 'Send failed',    cls: 'crmca-st--rose' },
};
function PlacementChip({ r }) {
  if (!r) return <span className="crmca-tbl-muted">—</span>;
  const m = r.folder ? (PLACE_META[r.folder] || { label: r.folder, cls: 'crmca-st--slate' }) : { label: 'Checking…', cls: 'crmca-st--slate' };
  const title = [r.error, r.warm_pair ? 'This seed has exchanged warmup mail with this sender, so it already trusts it. A cold prospect may see a worse result.' : null].filter(Boolean).join(' ');
  return <span className={'crmca-st ' + m.cls} title={title || undefined}>{m.label}{r.warm_pair ? ' *' : ''}</span>;
}

function PlacementSection({ campaignId }) {
  const { getToken } = useAuth();
  const [tests, setTests] = useState([]);
  const [running, setRunning] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/placement-tests?campaign_id=${campaignId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { const d = await r.json(); setTests(d.tests || []); }
    } catch (_) {}
  }, [campaignId, getToken]);
  useEffect(() => { load(); }, [load]);

  const latest = tests[0] || null;
  const shown = tests.find(t => t.id === openId) || latest;
  // Poll while a test is still being checked.
  useEffect(() => {
    if (!latest || latest.status === 'done') return;
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [latest, load]);

  async function run() {
    setRunning(true);
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/placement-tests`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaign_id: campaignId }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `Failed (${r.status})`);
      toast.success(`${d.sent} test ${d.sent === 1 ? 'email' : 'emails'} sent. Results arrive over the next few minutes.`);
      setOpenId(null);
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setRunning(false); }
  }

  const senders = shown ? [...new Set(shown.results.map(r => r.sender_email))] : [];
  const seeds = shown ? [...new Map(shown.results.map(r => [r.seed_email, r.seed_provider])).entries()] : [];
  const cell = (s, d) => shown.results.find(r => r.sender_email === s && r.seed_email === d);
  const counts = shown ? shown.counts || {} : {};
  const inboxN = (counts.inbox || 0) + (counts.other_tab || 0);
  const tabN = (counts.promotions || 0) + (counts.updates || 0) + (counts.social || 0) + (counts.forums || 0) + (counts.other || 0);
  const badN = (counts.spam || 0) + (counts.trash || 0) + (counts.missing || 0) + (counts.send_failed || 0);
  const anyWarm = shown && shown.results.some(r => r.warm_pair);

  return (
    <Section
      title="Inbox placement"
      right={
        <div className="crmca-sec-tools">
          {tests.length > 1 && (
            <select className="crmca-select crmca-select--sm" value={shown ? shown.id : ''} onChange={e => setOpenId(Number(e.target.value))}>
              {tests.map(t => <option key={t.id} value={t.id}>{new Date(t.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</option>)}
            </select>
          )}
          <button onClick={run} disabled={running} className="crmca-btn crmca-btn--sm crmca-btn--primary">{running ? 'Sending…' : 'Run test'}</button>
        </div>
      }
    >
      {!shown ? (
        <div className="crmca-muted">Sends this campaign's first email, exactly as a contact gets it, from each sending mailbox to your seed inboxes, then shows where it landed. Mark Google Workspace or Microsoft 365 mailboxes as seeds in Deliverability first.</div>
      ) : (
        <>
          <div className="crmca-sec-tools" style={{ marginBottom: 10, flexWrap: 'wrap' }}>
            <span className="crmca-st crmca-st--green">Inbox {inboxN}</span>
            {tabN > 0 && <span className="crmca-st crmca-st--amber">Tabs or other folder {tabN}</span>}
            {badN > 0 && <span className="crmca-st crmca-st--rose">Spam or not delivered {badN}</span>}
            <span className="crmca-tbl-muted">
              {shown.inbox_rate != null ? `${shown.inbox_rate}% inbox · ` : ''}"{shown.subject}"
              {shown.status !== 'done' ? ' · still checking, refreshes every 20 seconds' : ''}
            </span>
          </div>
          <table className="crmca-tbl">
            <thead>
              <tr>
                <th>From</th>
                {seeds.map(([email, prov]) => <th key={email}>{prov === 'microsoft' ? 'Microsoft 365' : 'Google'} · {email}</th>)}
              </tr>
            </thead>
            <tbody>
              {senders.map(s => (
                <tr key={s}>
                  <td>{s}</td>
                  {seeds.map(([d]) => <td key={d}><PlacementChip r={cell(s, d)} /></td>)}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="crmca-cfg-hint">
            Checked every 2 minutes. Not found after 30 minutes shows as Not delivered.
            {anyWarm ? ' * This seed has exchanged warmup mail with that sender, so it already trusts it; a cold prospect may see a worse result.' : ''}
          </div>
        </>
      )}
    </Section>
  );
}

function Section({ title, right, children }) {
  return (
    <div className="crmca-section">
      <div className="crmca-section-head">
        <div className="crmca-section-title">{title}</div>
        {right}
      </div>
      <div className="crmca-section-body">{children}</div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div className="crmca-field">
      <div className="crmca-field-lbl">{label}</div>
      <div className="crmca-field-val">{children}</div>
    </div>
  );
}
function Stat({ label, value, sub, accent }) {
  return (
    <div className="crmca-stat">
      <div className="crmca-stat-lbl">{label}</div>
      <div className="crmca-stat-val" style={accent ? { color: accent } : undefined}>{value}</div>
      {sub && <div className="crmca-stat-sub">{sub}</div>}
    </div>
  );
}

function ConversationModal({ convoData, convoLoading, onClose, activity }) {
  return (
    <div className="crmca-ovl" onClick={onClose}>
      <div className="crmca-convo" onClick={e => e.stopPropagation()}>
        <div className="crmca-convo-head">
          <div>
            {convoData && convoData.contact ? (
              <>
                <div className="crmca-convo-name">{convoData.contact.full_name || convoData.contact.email}</div>
                <div className="crmca-convo-sub">
                  {convoData.contact.email}
                  {convoData.contact.job_title ? ` · ${convoData.contact.job_title}` : ''}
                  {convoData.contact.company_name ? ` · ${convoData.contact.company_name}` : ''}
                </div>
              </>
            ) : <div className="crmca-convo-name">Conversation</div>}
          </div>
          <button onClick={onClose} className="crmca-convo-x">×</button>
        </div>
        <div className="crmca-convo-body">
          {!convoLoading && activity && (
            <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid var(--crm-line)' }}>
              <div className="crmca-cfg-lbl" style={{ marginBottom: 6 }}>Activity</div>
              <ActivityTimeline items={activity} />
            </div>
          )}
          {convoLoading ? (
            <div className="crmca-muted">Loading conversation...</div>
          ) : !convoData || !convoData.messages || convoData.messages.length === 0 ? (
            <div className="crmca-muted">No messages yet for this contact in this campaign.</div>
          ) : (
            convoData.messages.map(m => (
              <div key={`${m.direction}-${m.id}`} className={'crmca-msg crmca-msg--' + (m.direction === 'outbound' ? 'out' : 'in')}>
                <div className="crmca-msg-meta">
                  <div>
                    <strong className={'crmca-msg-dir crmca-msg-dir--' + (m.direction === 'outbound' ? 'out' : 'in')}>{m.direction === 'outbound' ? 'Sent' : 'Received'}</strong>
                    {m.step_number != null ? ` · Step ${m.step_number}` : ''}
                    {m.status ? ` · ${m.status}` : ''}
                  </div>
                  <div>{m.occurred_at ? new Date(m.occurred_at).toLocaleString('en-GB') : ''}</div>
                </div>
                <div className="crmca-msg-subj">{m.subject || '(no subject)'}</div>
                <div className="crmca-msg-addr">{m.direction === 'outbound' ? `To ${m.to_email}` : `From ${m.from_email}`}</div>
                <div className="crmca-msg-body" dangerouslySetInnerHTML={{ __html: m.body_html || (m.body_text || '').replace(/\n/g, '<br>') }} />
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function CreateCampaignModal({ clientId, onClose, onCreated }) {
  const { getToken } = useAuth();
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [pools, setPools] = useState([]);
  const [mailboxes, setMailboxes] = useState([]);
  const [selectedMailboxIds, setSelectedMailboxIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [mbQuery, setMbQuery] = useState('');
  const [providerFilter, setProviderFilter] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const headers = { Authorization: `Bearer ${token}` };
        const [pRes, mRes] = await Promise.all([
          fetch(`${API_BASE}/clients/${clientId}/pools`, { headers }),
          fetch(`${API_BASE}/clients/${clientId}/mailboxes`, { headers }),
        ]);
        if (pRes.ok) setPools(await pRes.json());
        if (mRes.ok) { const data = await mRes.json(); setMailboxes(Array.isArray(data) ? data : data.mailboxes || []); }
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, [clientId, getToken]);

  const loadPool = async (poolId) => {
    if (!poolId) return;
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/pools/${poolId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('failed to load pool');
      const data = await r.json();
      const ids = (data.members || data.mailboxes || []).map(m => m.mailbox_id || m.id);
      setSelectedMailboxIds(new Set([...selectedMailboxIds, ...ids]));
    } catch (e) { setError(e.message); }
  };

  const toggleMailbox = (id) => {
    const next = new Set(selectedMailboxIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedMailboxIds(next);
  };

  const filteredMailboxes = mailboxes.filter(m => {
    if (providerFilter !== 'all' && m.provider !== providerFilter) return false;
    if (mbQuery) {
      const q = mbQuery.toLowerCase();
      if (!(m.email_address || '').toLowerCase().includes(q) && !(m.display_name || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const providers = Array.from(new Set(mailboxes.map(m => m.provider).filter(Boolean)));

  const create = async () => {
    if (!name.trim()) { setError('Name required'); setStep(1); return; }
    if (selectedMailboxIds.size === 0) { setError('Pick at least one mailbox'); return; }
    setSaving(true); setError(null);
    try {
      const token = await getToken();
      const firstMailboxId = Array.from(selectedMailboxIds)[0];
      const createRes = await fetch(`${API_BASE}/clients/${clientId}/campaigns`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), description: description.trim() || null, mailbox_id: firstMailboxId }),
      });
      if (!createRes.ok) { const err = await createRes.json().catch(() => ({})); throw new Error(err.error || `Create failed (${createRes.status})`); }
      const created = await createRes.json();
      const newCampaignId = created.campaign.id;
      const attachRes = await fetch(`${API_BASE}/campaigns/${newCampaignId}/mailboxes`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ mailbox_ids: Array.from(selectedMailboxIds) }),
      });
      if (!attachRes.ok) { const err = await attachRes.json().catch(() => ({})); throw new Error(err.error || `Mailbox attach failed (${attachRes.status})`); }
      toast.success('Campaign created as draft');
      onCreated(newCampaignId);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const statusColour = (s) => s === 'active' ? 'var(--crm-green)' : s === 'error' ? 'var(--crm-rose)' : 'var(--crm-faint)';

  return (
    <div className="crmca-ovl" onClick={onClose}>
      <div className="crmca-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="crmca-dialog-head">
          <h3>New campaign</h3>
          <div className="crmca-dialog-step">Step {step} of 2</div>
        </div>
        <div className="crmca-wizbar">
          <div className={'crmca-wizbar-seg' + (step >= 1 ? ' crmca-wizbar-seg--on' : '')} />
          <div className={'crmca-wizbar-seg' + (step >= 2 ? ' crmca-wizbar-seg--on' : '')} />
        </div>

        {loading ? <div className="crmca-muted">Loading...</div> : (
          <>
            {step === 1 && (
              <div className="crmca-dialog-scroll">
                <div className="crmca-form-row">
                  <label className="crmca-form-lbl">Name</label>
                  <input value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="e.g. Q1 outreach — UK accountants" className="crmca-input" />
                </div>
                <div className="crmca-form-row">
                  <label className="crmca-form-lbl">Description (optional)</label>
                  <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What is this campaign for?" className="crmca-input" />
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="crmca-dialog-mb">
                <label className="crmca-form-lbl">Sending mailboxes</label>
                <div className="crmca-cfg-hint">Pick the mailboxes this campaign will rotate across. {selectedMailboxIds.size > 0 && <strong>{selectedMailboxIds.size} selected.</strong>}</div>
                <PoolPicker pools={pools} onPick={loadPool} />
                <div className="crmca-mb-tools">
                  <input type="text" placeholder="Search mailboxes..." value={mbQuery} onChange={(e) => setMbQuery(e.target.value)} className="crmca-input" />
                  {providers.length > 1 && (
                    <select value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)} className="crmca-select">
                      <option value="all">All providers</option>
                      {providers.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  )}
                </div>
                <div className="crmca-mb-list">
                  {filteredMailboxes.map(m => {
                    const checked = selectedMailboxIds.has(m.id);
                    const disabled = m.status === 'disconnected';
                    return (
                      <label key={m.id} className={'crmca-mb-item' + (checked ? ' crmca-mb-item--on' : '') + (disabled ? ' crmca-mb-item--dis' : '')}>
                        <input type="checkbox" checked={checked} disabled={disabled} onChange={() => toggleMailbox(m.id)} />
                        <div className="crmca-mb-info">
                          <div className="crmca-mb-email">{m.email_address}{m.display_name && <span className="crmca-tbl-sub"> — {m.display_name}</span>}</div>
                          <div className="crmca-mb-meta">
                            <span className="crmca-cap">{m.provider || '—'}</span><span>·</span>
                            <span style={{ color: statusColour(m.status) }}>{m.status || 'unknown'}</span><span>·</span>
                            <span>cap {m.daily_cap ?? '—'}</span>
                            {m.owner_type === 'webcite' && <><span>·</span><span className="crmca-shared">shared</span></>}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {error && <div className="crmca-err">{error}</div>}

            <div className="crmca-dialog-foot">
              <button onClick={onClose} disabled={saving} className="crmca-btn">Cancel</button>
              <div className="crmca-dialog-foot-r">
                {step === 2 && <button onClick={() => setStep(1)} disabled={saving} className="crmca-btn">Back</button>}
                {step === 1 && <button onClick={() => { if (!name.trim()) { setError('Name required'); return; } setError(null); setStep(2); }} className="crmca-btn crmca-btn--primary">Next: pick mailboxes</button>}
                {step === 2 && <button onClick={create} disabled={saving || selectedMailboxIds.size === 0} className="crmca-btn crmca-btn--primary">{saving ? 'Creating...' : `Create campaign (${selectedMailboxIds.size})`}</button>}
              </div>
            </div>
            <div className="crmca-cfg-hint">Created as <strong>draft</strong>. Configure schedule and audience here, then launch.</div>
          </>
        )}
      </div>
    </div>
  );
}

function AddMailboxesModal({ clientId, campaignId, existingMailboxIds, onClose, onAdded }) {
  const { getToken } = useAuth();
  const [mailboxes, setMailboxes] = useState([]);
  const [pools, setPools] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [query, setQuery] = useState('');
  const [providerFilter, setProviderFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const headers = { Authorization: `Bearer ${token}` };
        const [pRes, mRes] = await Promise.all([
          fetch(`${API_BASE}/clients/${clientId}/pools`, { headers }),
          fetch(`${API_BASE}/clients/${clientId}/mailboxes`, { headers }),
        ]);
        if (pRes.ok) setPools(await pRes.json());
        if (mRes.ok) { const data = await mRes.json(); setMailboxes(Array.isArray(data) ? data : data.mailboxes || []); }
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, [clientId, getToken]);

  const loadPool = async (poolId) => {
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/pools/${poolId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) return;
      const data = await r.json();
      const ids = (data.members || data.mailboxes || []).map(m => m.mailbox_id || m.id).filter(id => !existingMailboxIds.has(id));
      setSelected(new Set([...selected, ...ids]));
    } catch (_) {}
  };

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const available = mailboxes.filter(m => !existingMailboxIds.has(m.id));
  const filtered = available.filter(m => {
    if (providerFilter !== 'all' && m.provider !== providerFilter) return false;
    if (query) {
      const q = query.toLowerCase();
      if (!(m.email_address || '').toLowerCase().includes(q) && !(m.display_name || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const providers = Array.from(new Set(available.map(m => m.provider).filter(Boolean)));

  const add = async () => {
    if (selected.size === 0) return;
    setSaving(true); setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/campaigns/${campaignId}/mailboxes`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ mailbox_ids: Array.from(selected) }),
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || `Add failed (${res.status})`); }
      toast.success(`Added ${selected.size} mailbox${selected.size === 1 ? '' : 'es'}`);
      onAdded();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const statusColour = (s) => s === 'active' ? 'var(--crm-green)' : s === 'error' ? 'var(--crm-rose)' : 'var(--crm-faint)';

  return (
    <div className="crmca-ovl" onClick={onClose}>
      <div className="crmca-dialog" onClick={(e) => e.stopPropagation()}>
        <h3 className="crmca-dialog-h3">Add mailboxes to campaign</h3>
        {loading ? <div className="crmca-muted">Loading...</div> : (
          <>
            <PoolPicker pools={pools} onPick={loadPool} />
            <div className="crmca-mb-tools">
              <input type="text" placeholder="Search mailboxes..." value={query} onChange={(e) => setQuery(e.target.value)} className="crmca-input" />
              {providers.length > 1 && (
                <select value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)} className="crmca-select">
                  <option value="all">All providers</option>
                  {providers.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              )}
            </div>
            <div className="crmca-mb-list">
              {available.length === 0 ? (
                <div className="crmca-mb-empty">All available mailboxes are already attached.</div>
              ) : filtered.length === 0 ? (
                <div className="crmca-mb-empty">No mailboxes match.</div>
              ) : filtered.map(m => {
                const checked = selected.has(m.id);
                const disabled = m.status === 'disconnected';
                return (
                  <label key={m.id} className={'crmca-mb-item' + (checked ? ' crmca-mb-item--on' : '') + (disabled ? ' crmca-mb-item--dis' : '')}>
                    <input type="checkbox" checked={checked} disabled={disabled} onChange={() => toggle(m.id)} />
                    <div className="crmca-mb-info">
                      <div className="crmca-mb-email">{m.email_address}{m.display_name && <span className="crmca-tbl-sub"> — {m.display_name}</span>}</div>
                      <div className="crmca-mb-meta">
                        <span className="crmca-cap">{m.provider || '—'}</span><span>·</span>
                        <span style={{ color: statusColour(m.status) }}>{m.status || 'unknown'}</span><span>·</span>
                        <span>cap {m.daily_cap ?? '—'}</span>
                        {m.owner_type === 'webcite' && <><span>·</span><span className="crmca-shared">shared</span></>}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
            {error && <div className="crmca-err">{error}</div>}
            <div className="crmca-dialog-foot">
              <div className="crmca-muted">{selected.size} selected</div>
              <div className="crmca-dialog-foot-r">
                <button onClick={onClose} disabled={saving} className="crmca-btn">Cancel</button>
                <button onClick={add} disabled={saving || selected.size === 0} className="crmca-btn crmca-btn--primary">{saving ? 'Adding...' : `Add ${selected.size > 0 ? selected.size : ''}`}</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
